import { useEffect, useState } from 'react';

import { SelectOption } from '../../../models/select-option';
import TextField from '../../ui/text-field/text-field';
import { Search, ArrowRepeat, XCircleFill } from 'react-bootstrap-icons';

interface AutocompleteSearcherProps {
  label?: string;
  labelPosition?: 'left' | 'top';
  className?: string;
  placeholder?: string;
  initialValue?: string;
  onSearchValueChanged?: (searchValue: string) => void;
  options?: SelectOption[];
  selectedOption?: SelectOption;
  onOptionSelectedChanged?: (optionSelected: SelectOption | undefined) => void;
  loading?: boolean;
  waitInputThresold?: number;
  autoFocus?: boolean;
}

const KEY_ENTER = 'Enter';
const KEY_ARROW_UP = 'ArrowUp';
const KEY_ARROW_DOWN = 'ArrowDown';
const KEY_ESC = 'Escape';
const DEFAULT_HIGHTLIGHTED_OPTION_INDEX = -1;

const DEFAULT_WAIT_THRESOLD_MS = 400;
export const AutocompleteSearcher: React.FC<AutocompleteSearcherProps> = ({
  label,
  labelPosition = 'left',
  className,
  placeholder,
  initialValue,
  onSearchValueChanged,
  options = [],
  selectedOption,
  onOptionSelectedChanged,
  loading = false,
  waitInputThresold = DEFAULT_WAIT_THRESOLD_MS,
  autoFocus = false,
}) => {
  const [searchTerm, setSearchTerm] = useState(initialValue ?? '');
  const [searchOnFocus, setSearchOnFocus] = useState<boolean>(true);
  const [highlightedOptionIndex, setHighlightedOptionIndex] = useState(
    DEFAULT_HIGHTLIGHTED_OPTION_INDEX
  );

  let searchInputTimeOut: NodeJS.Timeout;

  useEffect(() => {
    setHighlightedOptionIndex(DEFAULT_HIGHTLIGHTED_OPTION_INDEX);
  }, [options]);

  useEffect(() => {
    initialValue ? setSearchTerm(initialValue) : setSearchTerm('');
  }, [initialValue]);

  const handleKeyDown = (evt: KeyboardEvent) => {
    switch (evt.key) {
      case KEY_ARROW_UP:
        highlightedOptionIndex - 1 > DEFAULT_HIGHTLIGHTED_OPTION_INDEX &&
          setHighlightedOptionIndex((highlightedOptionIndex) => highlightedOptionIndex - 1);
        break;

      case KEY_ARROW_DOWN:
        highlightedOptionIndex < options?.length - 1 &&
          setHighlightedOptionIndex((highlightedOptionIndex) => highlightedOptionIndex + 1);
        break;

      case KEY_ENTER:
        if (onOptionSelectedChanged && options[highlightedOptionIndex]) {
          onOptionSelectedChanged(options[highlightedOptionIndex]);
          setSearchOnFocus(false);
        }
        break;

      case KEY_ESC:
        resetSearch();
        setSearchOnFocus(false);
        break;

      default:
        setSearchOnFocus(true);
        break;
    }
  };

  const handleNewInput = (newInput: string) => {
    setSearchTerm(newInput);
    clearTimeout(searchInputTimeOut);
    searchInputTimeOut = setTimeout(() => {
      onSearchValueChanged && onSearchValueChanged(newInput);
    }, waitInputThresold);
  };

  const handleOptionSelected = (optionSelected: SelectOption) => {
    setSearchTerm(optionSelected.label);
    onOptionSelectedChanged && onOptionSelectedChanged(optionSelected);
  };

  const handleFocus = () => {
    setSearchOnFocus(true);
  };

  const handleBlur = () => {
    setSearchOnFocus(false);
  };

  const resetSearch = () => {
    setSearchTerm('');
    onSearchValueChanged && onSearchValueChanged('');
    onOptionSelectedChanged && onOptionSelectedChanged(undefined);
  };

  const inputValue = selectedOption?.label ?? searchTerm;

  return (
    <div className={`${className ?? ''}`}>
      {label && <div className="">{label}</div>}
      <div className="relative">
        {searchTerm && !loading && (
          <div
            onMouseDown={resetSearch}
            className="absolute right-0 top-0 flex h-full cursor-pointer items-center pr-3 text-madriwaGreenLight"
          >
            <XCircleFill className="h-5 w-5" />
          </div>
        )}
        {!searchTerm && !loading && (
          <div className="absolute right-0 top-0 flex h-full items-center pr-3 text-madriwaGreenLight">
            <Search className="h-5 w-5" />
          </div>
        )}
        {loading && (
          <div className="absolute right-0 top-0 flex h-full items-center pr-3 text-madriwaGreenLight">
            <ArrowRepeat className="animate-spin text-xl" />
          </div>
        )}
        <TextField
          className="mr-5 w-full"
          placeholder={placeholder}
          onChange={handleNewInput}
          onFocus={handleFocus}
          onBlur={handleBlur}
          onKeyDown={handleKeyDown}
          value={inputValue}
          autoFocus={autoFocus}
        />
        {searchOnFocus && options.length > 0 && (
          <ul className="absolute w-full border border-[#c1c1c1] bg-[#ffffff]">
            {options.map((option, index) => (
              <li
                key={index}
                className=":hover-searchTextHoverColor cursor-pointer border-b  border-[#c1c1c1] bg-searchBgColor px-3 py-2 text-searchTextColor transition-all duration-300 ease-out hover:bg-searchBgHoverColor hover:text-searchTextHoverColor"
                onMouseDown={() => handleOptionSelected(option)}
              >
                {option.label}
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
};

export default AutocompleteSearcher;
